import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import {
  DeliverySlot,
  PostalCodeTypes,
  useQueryDeliveryMethods,
  useQueryDeliverySlots
} from 'modules/delivery'
import Button from 'shared/ui/Button'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { RadioGroup } from 'shared/ui/RadioGroup'
import type { RadioGroupProps } from 'shared/ui/RadioGroup/RadioGroup'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import { getCartItemsPrice } from '../../../rules'
import useCartStore from '../../../store/cartStore'
import { Cart, CartItem } from '../../../types'
import SubstitutesByDeliveryMethodModal from '../../substitutes/components/SubstitutesByDeliveryMethodModal'
import SubstitutesModal from '../../substitutes/components/SubstitutesModal'

import CartDeliveryDays from './CartDeliveryDays'
import CartDeliveryMethod from './CartDeliveryMethod'

import styles from './CartDeliveryDate.module.scss'

const isAddressSupported = (
  selectedDeliveryMethodId: string,
  availableDeliveryMethods: PostalCodeTypes[]
) => {
  return !!availableDeliveryMethods.find(
    deliveryMethod => deliveryMethod.id === selectedDeliveryMethodId
  )
}

interface IsFreeDeliveryMethodProps {
  deliveryMethod: PostalCodeTypes
  cart: Cart | null
}

const isFreeDeliveryMethod = ({
  deliveryMethod,
  cart
}: IsFreeDeliveryMethodProps) => {
  const totalValue = getCartItemsPrice({
    paymentValue: cart?.paymentValue,
    deliveryPrice: cart?.deliveryCost
  })

  return (
    typeof deliveryMethod?.minOrderValueForFreeDelivery?.value === 'number' &&
    deliveryMethod?.minOrderValueForFreeDelivery.value <= totalValue
  )
}

const CartDeliveryDate = (): React.ReactNode => {
  const { addSlotToCart, cart } = useCartStore()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const [selectedDeliveryMethodId, setSelectedDeliveryMethodId] = useState<
    string | null
  >(null)
  const [openReplacementModalTrigger, setOpenReplacementModalTrigger] =
    useState<number>(0)

  const [
    openReplacementModalTriggerForDeliveryMethod,
    setOpenReplacementModalTriggerForDeliveryMethod
  ] = useState<number>(0)

  const deliveryQuery = useQueryDeliveryMethods({
    city: cart?.deliveryAddress?.city || '',
    postalCode: cart?.deliveryAddress?.postalCode || '',
    token
  })

  const deliverySlotsQuery = useQueryDeliverySlots(
    selectedDeliveryMethodId,
    token
  )

  useEffect(() => {
    if (!deliveryQuery.data) return

    const availableDeliveryMethods = deliveryQuery.data

    if (selectedDeliveryMethodId) {
      if (
        !isAddressSupported(selectedDeliveryMethodId, availableDeliveryMethods)
      ) {
        setSelectedDeliveryMethodId(null)
      }
    } else {
      const cartDeliveryMethodId = cart?.deliverySlot?.deliveryMethodId

      if (cartDeliveryMethodId) {
        if (
          isAddressSupported(cartDeliveryMethodId, availableDeliveryMethods)
        ) {
          setSelectedDeliveryMethodId(cartDeliveryMethodId)
        }
      }
    }
  }, [
    selectedDeliveryMethodId,
    deliveryQuery.data,
    cart?.deliverySlot?.deliveryMethodId
  ])

  const handleChangeDeliveryMethod = useCallback(
    (deliveryMethodId: string) => {
      setSelectedDeliveryMethodId(deliveryMethodId)
    },
    [setSelectedDeliveryMethodId]
  )

  const handleChangeDeliverySlot = useCallback(
    async (slot: DeliverySlot | null) => {
      if (!slot) return

      try {
        await addSlotToCart(slot, session)
      } catch (err) {
        toast.error(getApiErrors(err))
      }
    },
    [addSlotToCart, session]
  )

  // Create a lightweight dependency that only changes when items are added/removed/replaced
  const cartItemsSignature = useMemo(() => {
    if (!cart?.items) return 0

    let hash = cart.items.length

    for (const item of cart.items) {
      const str = `${item.id}${item.product.id}${item.quantity}`

      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) & 0xffffffff
      }
    }

    return hash
  }, [cart?.items])

  const unavailableProductsByDeliveryId = useMemo(() => {
    if (!deliveryQuery.data || !cart?.items) return {}

    const unavailableDeliveries: Record<string, CartItem[]> = {}

    deliveryQuery.data.forEach(delivery => {
      cart.items.forEach(item => {
        const itemDeliveryMethods = item.product.deliveryMethods

        const isProductContainDeliveryMethod = itemDeliveryMethods.some(
          deliveryMethod => deliveryMethod.id === delivery.id
        )

        if (isProductContainDeliveryMethod) return

        if (!unavailableDeliveries[delivery.id]) {
          unavailableDeliveries[delivery.id] = [item]
        } else {
          unavailableDeliveries[delivery.id].push(item)
        }
      })
    })

    return unavailableDeliveries
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItemsSignature, deliveryQuery.data])

  useEffect(() => {
    if (cart?.deliverySlot?.hasUnavailableCartItems) {
      setOpenReplacementModalTrigger(prev => prev + 1)
    }
  }, [cart?.deliverySlot])

  useEffect(() => {
    const cartDeliveryMethodId = cart?.deliverySlot?.deliveryMethod?.id

    if (
      cartDeliveryMethodId &&
      unavailableProductsByDeliveryId[cartDeliveryMethodId]?.length > 0
    ) {
      setOpenReplacementModalTriggerForDeliveryMethod(prev => prev + 1)
    }
  }, [unavailableProductsByDeliveryId, cart?.deliverySlot?.deliveryMethod?.id])

  const sortedDeliveryMethods = useMemo(() => {
    return (
      deliveryQuery.data?.sort((a, b) => {
        const aLength = unavailableProductsByDeliveryId[a.id]?.length || 0
        const bLength = unavailableProductsByDeliveryId[b.id]?.length || 0
        const result = aLength - bLength

        return result
      }) || []
    )
  }, [deliveryQuery.data, unavailableProductsByDeliveryId])

  const isLoading = useMemo(() => {
    return (
      isNilOrEmpty(cart) ||
      deliveryQuery.isLoading ||
      deliveryQuery.isRefetching
    )
  }, [cart, deliveryQuery.isLoading, deliveryQuery.isRefetching])

  const noProvidedAddress =
    !isLoading &&
    isNotNilOrEmpty(cart) &&
    !isNotNilOrEmpty(cart?.deliveryAddress)

  const isDeliveryAvailable =
    !noProvidedAddress &&
    deliveryQuery.isSuccess &&
    isNotNilOrEmpty(deliveryQuery.data)

  const isDeliveryNotAvailable =
    !noProvidedAddress &&
    deliveryQuery.isSuccess &&
    isNilOrEmpty(deliveryQuery.data)

  const isSlotsEmpty = useMemo(() => {
    return Object.keys(deliverySlotsQuery.data || {}).length === 0
  }, [deliverySlotsQuery.data])

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>Wybierz formę, datę i godzinę dostawy</h2>
      {isLoading && <LoadingIndicator size={40} />}

      {noProvidedAddress && (
        <div className={styles.noDelivery}>
          Podaj swój adres, aby sprawdzić czy do Ciebie dojedziemy
        </div>
      )}

      {isDeliveryNotAvailable && (
        <div className={styles.noDelivery}>
          Na chwilę obecną nie jesteśmy w stanie dowieźć zakupów pod wskazany
          adres.
        </div>
      )}

      {isDeliveryAvailable && (
        <>
          <DeliveryMethodRadioGroup
            name='deliveryMethod'
            selectedValue={selectedDeliveryMethodId}
            onChange={value => handleChangeDeliveryMethod(value)}
            orientation='horizontal'
            hasRadioMark={false}
            variant='boxed'
          >
            {sortedDeliveryMethods?.map(deliveryMethod => (
              <CartDeliveryMethod
                isFreeDelivery={isFreeDeliveryMethod({ deliveryMethod, cart })}
                key={deliveryMethod.id}
                checked={deliveryMethod.id === selectedDeliveryMethodId}
                deliveryMethod={deliveryMethod}
                countUnavailableProductsInDelivery={
                  unavailableProductsByDeliveryId[deliveryMethod.id]?.length ||
                  null
                }
              />
            ))}
          </DeliveryMethodRadioGroup>
        </>
      )}

      {isDeliveryAvailable &&
        selectedDeliveryMethodId &&
        deliverySlotsQuery.data && (
          <>
            <CartDeliveryDays
              data={deliverySlotsQuery.data}
              selectedSlot={cart?.deliverySlot || null}
              setSelectedSlot={handleChangeDeliverySlot}
              showFreeBadge={
                !isFreeDeliveryMethod({
                  deliveryMethod: sortedDeliveryMethods.find(
                    method => method.id === selectedDeliveryMethodId
                  ) as PostalCodeTypes,
                  cart
                })
              }
            />

            {isSlotsEmpty && <div>Brak dostępnych terminów dostawy</div>}
          </>
        )}
      {selectedDeliveryMethodId &&
        unavailableProductsByDeliveryId[selectedDeliveryMethodId]?.length >
          0 && (
          <div className={styles.noDelivery}>
            <span>
              Część produktów (
              <b>
                {`${unavailableProductsByDeliveryId[selectedDeliveryMethodId].length}`}
              </b>
              ) nie jest dostępna w tej metodzie dostawy
            </span>
            <Button
              variant='primaryOutline'
              onClick={() =>
                setOpenReplacementModalTriggerForDeliveryMethod(
                  prev => prev + 1
                )
              }
            >
              Pokaż zamienniki
            </Button>
          </div>
        )}
      <SubstitutesModal modalOpenTrigger={openReplacementModalTrigger} />
      <SubstitutesByDeliveryMethodModal
        customProducts={true}
        deliveryMethodId={selectedDeliveryMethodId}
        modalOpenTrigger={openReplacementModalTriggerForDeliveryMethod}
        productsNotAvailable={
          (selectedDeliveryMethodId &&
            unavailableProductsByDeliveryId[selectedDeliveryMethodId]) ||
          null
        }
      />
    </div>
  )
}

export default CartDeliveryDate

const DeliveryMethodRadioGroup = ({
  className,
  ...props
}: RadioGroupProps) => (
  <RadioGroup
    className={clsx(styles.deliveryMethodRadioGroup, className)}
    {...props}
  />
)
